import React, { FC, useState, useMemo, ChangeEvent, useEffect, useRef } from 'react';
import {
  ComboBoxContainer,
  DocumentUploadsContainer,
  DocumentsInfoText,
  DocumentsSectionHeading,
  ErrorContainer,
} from './MultipleDocumentUploadWrapper.styles';

import { MultipleDocumentUploadWrapperTypes } from './types';
import {
  DocumentData,
  isIndividualRelatedEntityDocumentRequiredDependingOnAge,
} from 'helpers/documentHelper';
import FieldSelect from 'components/atoms/field-select';
import { useTheme } from 'styled-components';
import { Icon, Typography } from 'components/atoms';
import MultipleUploadOptionsSection from './MultipleUploadOptionsSection';
import { ThirdPartyType } from 'types';

const MultipleDocumentUploadWrapper: FC<MultipleDocumentUploadWrapperTypes> = ({
  thirdPartyId,
  thirdPartyData,
  categories,
  defaultOptionalCategoryId,
  selectedRelatedThirdParties,
  documentsConfig,
  error,
  isValid,
  value,
  showMandatoryDocuments = true,
  onChange,
  onPendingUploadsChange,
  onBlockingUploadsCountChange,
}: MultipleDocumentUploadWrapperTypes) => {
  const theme = useTheme();
  const latestDocumentsRef = useRef<DocumentData[]>(value ?? []);

  useEffect(() => {
    latestDocumentsRef.current = value ?? [];
  }, [value]);

  const errorMessage = error?.message;

  const [selectedThirdPartyOrEntityId, setSelectedThirdPartyOrEntityId] =
    useState<string>(thirdPartyId);
  const [isThirdPartySelected, setIsThirdPartySelected] = useState<boolean>(
    (showMandatoryDocuments && documentsConfig.isRequiredDocumentsForThirdParty) ||
      documentsConfig.isOptionalDocumentsForThirdParty
  );

  const thirdPartyAndEntitiesSelection = useMemo(() => {
    if (!documentsConfig || !thirdPartyData) {
      return [];
    }

    let options: { name: string; value: string }[] = [];

    if (
      (showMandatoryDocuments && documentsConfig.isRequiredDocumentsForThirdParty) ||
      documentsConfig.isOptionalDocumentsForThirdParty
    ) {
      options = [
        ...options,
        {
          name: thirdPartyData.name + ' (This Thirdparty)',
          value: thirdPartyData.id.toString(),
        },
      ];
    }

    if (showMandatoryDocuments && documentsConfig.isRequiredDocumentsForRelatedThirdParties) {
      const relatedThirdPartiesOptions = selectedRelatedThirdParties
        .filter((selectedRelatedEntity) => {
          if (
            selectedRelatedEntity.type === ThirdPartyType.Business &&
            documentsConfig.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsBusiness
              .length > 0
          ) {
            return true;
          }
          if (
            selectedRelatedEntity.type === ThirdPartyType.Individual &&
            documentsConfig.relatedThirdPartiesDocuments
              .linkedThirdPartyConfiguredDocumentsIndividual.length > 0
          ) {
            return true;
          }

          return false;
        })
        .map((selectedRelatedEntity) => ({
          name: selectedRelatedEntity.name,
          value: selectedRelatedEntity.id.toString(),
        }));

      const sortedREs = relatedThirdPartiesOptions.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );

      options = [...options, ...sortedREs];
    }
    return options;
  }, [thirdPartyData, selectedRelatedThirdParties, documentsConfig, showMandatoryDocuments]);

  const totalRequiredDocumentsCount = useMemo(() => {
    let count = 0;

    const selectedIndividualRelatedEntities = selectedRelatedThirdParties.filter(
      ({ type }) => type === ThirdPartyType.Individual
    );

    const selectedBusinessRelatedEntities = selectedRelatedThirdParties.filter(
      ({ type }) => type === ThirdPartyType.Business
    );

    if (showMandatoryDocuments && documentsConfig.isRequiredDocumentsForThirdParty) {
      if (thirdPartyData.type === ThirdPartyType.Individual) {
        count += documentsConfig.thirdPartyDocuments.thirdPartyConfiguredDocumentsIndividual.length;
      } else {
        count += documentsConfig.thirdPartyDocuments.thirdPartyConfiguredDocumentsBusiness.length;
      }
    }

    if (showMandatoryDocuments && documentsConfig.isRequiredDocumentsForRelatedThirdParties) {
      const individualRequiredDocumentsCount = selectedIndividualRelatedEntities.reduce(
        (acc, relatedEntity) => {
          const requiredDocuments =
            documentsConfig.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsIndividual.filter(
              (configuredDoc) =>
                isIndividualRelatedEntityDocumentRequiredDependingOnAge(
                  relatedEntity,
                  configuredDoc
                )
            );

          return acc + requiredDocuments.length;
        },
        0
      );
      const businessRequiredDocumentsCount =
        selectedBusinessRelatedEntities.length *
        documentsConfig.relatedThirdPartiesDocuments.linkedThirdPartyConfiguredDocumentsBusiness
          .length;

      count += individualRequiredDocumentsCount + businessRequiredDocumentsCount;
    }

    return count;
  }, [thirdPartyData.type, documentsConfig, selectedRelatedThirdParties, showMandatoryDocuments]);

  const handleAddDocuments = (newDocuments: DocumentData[]) => {
    const nextDocuments = [...latestDocumentsRef.current, ...newDocuments];

    latestDocumentsRef.current = nextDocuments;
    onChange(nextDocuments);
  };

  const handleRemoveDocument = (id: string, entityId: string, isOptional: boolean) => {
    const nextDocuments = [...latestDocumentsRef.current];
    const foundIndex = nextDocuments.findIndex(
      (doc) => doc.id === id && doc.entityId === entityId && doc.isOptionalDocument === isOptional
    );

    if (foundIndex === -1) {
      return;
    }

    nextDocuments.splice(foundIndex, 1);
    latestDocumentsRef.current = nextDocuments;
    onChange(nextDocuments);
  };

  const handleDocumentStatusChange = (
    id: string,
    fileStatus: string,
    errorMessage?: string | null
  ) => {
    const foundIndex = latestDocumentsRef.current.findIndex((doc) => doc.id === id);

    if (foundIndex === -1) {
      return;
    }

    const nextDocuments = [...latestDocumentsRef.current];
    nextDocuments[foundIndex] = { ...nextDocuments[foundIndex], fileStatus, errorMessage };
    latestDocumentsRef.current = nextDocuments;
    onChange(nextDocuments);
  };

  return (
    <DocumentUploadsContainer>
      <DocumentsSectionHeading>Document upload</DocumentsSectionHeading>
      {selectedRelatedThirdParties.length > 0 && (
        <>
          <DocumentsInfoText>
            {`You have ${totalRequiredDocumentsCount} documents to upload for all related entities created`}
          </DocumentsInfoText>
          <ComboBoxContainer>
            <FieldSelect
              valid={isValid}
              fullWidth
              placeholder=""
              ariaLabel="Document Category Dropdown"
              id="ThirdPartyAndEntities"
              options={thirdPartyAndEntitiesSelection}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                setSelectedThirdPartyOrEntityId(e.target.value);
                setIsThirdPartySelected(e.target.selectedIndex === 0);
              }}
            />
          </ComboBoxContainer>
        </>
      )}

      <MultipleUploadOptionsSection
        documentsConfig={documentsConfig}
        documents={value}
        categories={categories}
        defaultOptionalCategoryId={defaultOptionalCategoryId}
        mainThirdPartyId={thirdPartyData.id}
        thirdPartyType={thirdPartyData.type as ThirdPartyType}
        selectedRelatedThirdParties={selectedRelatedThirdParties}
        selectedThirdPartyId={selectedThirdPartyOrEntityId}
        isMainThirdPartySelected={isThirdPartySelected}
        onSuccessDocumentUpload={(document) => {
          handleAddDocuments([document]);
        }}
        onDeleteDocument={(id, entityId, isOptional) => {
          handleRemoveDocument(id, entityId, isOptional);
        }}
        onAttachDocuments={(documents) => {
          handleAddDocuments(documents);
        }}
        onDocumentStatusChange={handleDocumentStatusChange}
        onPendingUploadsChange={onPendingUploadsChange}
        onBlockingUploadsCountChange={onBlockingUploadsCountChange}
        showMandatoryDocuments={showMandatoryDocuments}
        error={error}
      />

      {errorMessage && (
        <ErrorContainer>
          <Icon className="risk-icon" icon="risk" size="xxs" colour={theme.colours.redRampage} />
          <Typography value={errorMessage} tag="p" />
        </ErrorContainer>
      )}
    </DocumentUploadsContainer>
  );
};

export default MultipleDocumentUploadWrapper;
